import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import { format } from 'date-fns';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeft, Twitter, Linkedin, Facebook, Clock, Calendar, ChevronRight } from 'lucide-react';
import { blogPosts } from '../../data/blogPosts';

// ---------------------------------------------------------------------------
// Helper: Extract H2 headings from markdown string for dynamic TOC
// ---------------------------------------------------------------------------
function extractHeadings(markdown) {
    const lines = markdown.split('\n');
    const headings = [];
    for (const line of lines) {
        const match = line.match(/^## (.+)$/);
        if (match) {
            const text = match[1].trim();
            const id = text
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-');
            headings.push({ text, id });
        }
    }
    return headings;
}

const BlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const post = blogPosts.find(p => p.slug === slug);

    // Scroll progress
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

    // Dynamic TOC active heading tracking
    const [activeId, setActiveId] = useState('');

    const headings = post ? extractHeadings(post.content) : [];

    useEffect(() => {
        if (!post) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter(e => e.isIntersecting);
                if (visible.length > 0) {
                    setActiveId(visible[0].target.id);
                }
            },
            { rootMargin: '-100px 0px -60% 0px', threshold: 0.1 }
        );

        const timer = setTimeout(() => {
            const elements = document.querySelectorAll('article h2[id]');
            Array.from(elements).forEach(el => observer.observe(el));
        }, 400);

        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, [post]);

    useEffect(() => {
        if (!post) navigate('/resources/blog');
    }, [post, navigate]);

    if (!post) return null;

    const scrollToHeading = (e, id) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
            const y = el.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "image": post.image,
        "datePublished": post.date,
        "author": { "@type": "Person", "name": post.author.name },
        "description": post.excerpt
    };

    return (
        <article className="min-h-screen bg-[#f5f3f0] dark:bg-[#0a1220] font-sans transition-colors duration-300">
            <Helmet>
                <title>{post.title} | DataHelix Resources</title>
                <meta name="description" content={post.excerpt} />
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            {/* ── Progress Bar ── */}
            <motion.div
                className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 origin-left z-50"
                style={{ scaleX }}
            />

            {/* ── Hero Banner ── */}
            <header className="relative overflow-hidden bg-gradient-to-br from-[#0f1c2e] via-[#1e3a5f] to-[#0a1220] pt-28 pb-20 md:pt-36 md:pb-28">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-orange-500/15 blur-[120px] rounded-full pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-amber-500/10 blur-[100px] rounded-full pointer-events-none" />

                <div className="relative z-10 max-w-4xl mx-auto px-6">
                    {/* Breadcrumbs */}
                    <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
                        <Link to="/resources/blog" className="hover:text-white transition-colors flex items-center gap-1">
                            <ArrowLeft size={14} /> Resources
                        </Link>
                        <ChevronRight size={14} className="text-gray-600" />
                        <span className="text-orange-400 font-medium truncate max-w-[260px]">{post.title}</span>
                    </nav>

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 mb-6">
                        <span className="px-3 py-1 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-300 text-xs font-bold tracking-wider uppercase">
                            {post.category}
                        </span>
                        <span className="flex items-center gap-2 text-gray-400 text-sm">
                            <Calendar size={14} /> {format(new Date(post.date), 'MMMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-2 text-gray-400 text-sm">
                            <Clock size={14} /> {post.readTime}
                        </span>
                    </div>

                    {/* Title */}
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-8 text-white">
                        {post.title}
                    </h1>

                    {/* Author */}
                    <div className="flex items-center gap-4">
                        <img src={post.author.avatar} alt={post.author.name} className="w-12 h-12 rounded-full border-2 border-orange-500/40 shadow-lg" />
                        <div>
                            <div className="text-white font-semibold">{post.author.name}</div>
                            <div className="text-sm text-orange-400/80">{post.author.role}</div>
                        </div>
                    </div>
                </div>
            </header>

            {/* ── Content: Article + Sidebar ── */}
            <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10 -mt-10 pb-24 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-10">

                {/* ── Article Card ── */}
                <main className="bg-white dark:bg-[#162032] rounded-2xl shadow-xl dark:shadow-2xl border border-gray-200/60 dark:border-white/5 overflow-hidden">

                    {/* Cover Image */}
                    <img src={post.image} alt={post.title} className="w-full" />

                    {/* Body */}
                    <div className="px-6 md:px-10 lg:px-14 py-8 md:py-12">
                        <div className="prose prose-lg dark:prose-invert max-w-none
                            prose-headings:font-bold prose-headings:tracking-tight
                            prose-h2:text-[1.65rem] prose-h2:text-gray-900 dark:prose-h2:text-white prose-h2:mt-14 prose-h2:mb-5 prose-h2:pl-4 prose-h2:border-l-4 prose-h2:border-orange-500 prose-h2:py-1
                            prose-h3:text-lg prose-h3:text-gray-800 dark:prose-h3:text-gray-200 prose-h3:mt-8 prose-h3:mb-3
                            prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-p:leading-[1.85] prose-p:text-[1.05rem]
                            prose-a:text-orange-600 dark:prose-a:text-orange-400 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                            prose-strong:text-gray-900 dark:prose-strong:text-white
                            prose-code:text-orange-700 dark:prose-code:text-orange-300 prose-code:bg-orange-50 dark:prose-code:bg-orange-500/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
                            prose-pre:bg-[#0a1220] prose-pre:border prose-pre:border-gray-800 prose-pre:rounded-xl prose-pre:shadow-lg
                            prose-blockquote:border-l-4 prose-blockquote:border-l-orange-500 prose-blockquote:bg-gradient-to-r prose-blockquote:from-orange-50 prose-blockquote:to-transparent dark:prose-blockquote:from-orange-500/5 dark:prose-blockquote:to-transparent prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic
                            prose-li:text-gray-600 dark:prose-li:text-gray-300 prose-li:marker:text-orange-500 prose-li:leading-relaxed prose-li:my-1
                            prose-ul:space-y-1 prose-ul:my-4 prose-ol:my-4
                            [&_li>p]:m-0
                            prose-table:border-collapse prose-table:w-full prose-table:rounded-xl prose-table:overflow-hidden
                            prose-thead:bg-gradient-to-r prose-thead:from-[#f97316] prose-thead:to-[#fb923c]
                            prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:text-sm prose-th:font-bold prose-th:tracking-wide prose-th:text-white
                            prose-td:px-4 prose-td:py-3 prose-td:text-sm prose-td:border-b prose-td:border-gray-100 dark:prose-td:border-white/5
                            prose-tr:even:bg-gray-50 dark:prose-tr:even:bg-white/[0.02]
                        ">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
                                {post.content}
                            </ReactMarkdown>
                        </div>

                        {/* Share */}
                        <div className="mt-14 pt-8 border-t border-gray-100 dark:border-white/10">
                            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Share this article</h3>
                            <div className="flex gap-3">
                                {(() => {
                                    const url = encodeURIComponent(window.location.href);
                                    const title = encodeURIComponent(post.title);
                                    const links = [
                                        { Icon: Twitter, href: `https://twitter.com/intent/tweet?url=${url}&text=${title}` },
                                        { Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
                                        { Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
                                    ];
                                    return links.map(({ Icon, href }, i) => (
                                        <a
                                            key={i}
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-3 rounded-xl bg-gray-50 dark:bg-white/5 hover:bg-orange-50 dark:hover:bg-orange-500/10 text-gray-500 hover:text-orange-600 dark:hover:text-orange-400 transition-all border border-gray-200 dark:border-white/10 hover:border-orange-200 dark:hover:border-orange-500/30"
                                        >
                                            <Icon size={18} />
                                        </a>
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>
                </main>

                {/* ── Sidebar ── */}
                <aside className="hidden lg:block">
                    <div className="sticky top-24 space-y-6">

                        {/* TOC Card */}
                        <div className="bg-white dark:bg-[#162032] rounded-xl border border-gray-200/60 dark:border-white/5 shadow-md dark:shadow-none p-5">
                            <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">
                                In this article
                            </h3>
                            <nav className="space-y-0.5">
                                {headings.map((h) => (
                                    <a
                                        key={h.id}
                                        href={`#${h.id}`}
                                        onClick={(e) => scrollToHeading(e, h.id)}
                                        className={`block text-[13px] py-2 px-3 rounded-lg transition-all duration-200 leading-snug ${activeId === h.id
                                            ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 font-semibold'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
                                            }`}
                                    >
                                        {h.text}
                                    </a>
                                ))}
                            </nav>
                        </div>

                        {/* Start Practicing CTA */}
                        <div className="bg-gradient-to-br from-[#0f1c2e] to-[#1e3a5f] rounded-xl p-5 border border-white/5 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/15 blur-[40px] rounded-full pointer-events-none" />
                            <h4 className="text-white font-bold mb-1 text-sm relative z-10">Ready to Level Up?</h4>
                            <p className="text-xs text-gray-400 mb-4 leading-relaxed relative z-10">
                                Apply what you've learned with hands-on SQL challenges on real datasets.
                            </p>
                            <Link to="/exercises/thinking-in-data" className="block w-full py-2.5 rounded-lg bg-gradient-to-r from-[#f97316] to-[#fb923c] hover:from-[#ea580c] hover:to-[#f97316] text-white font-bold text-sm transition-all shadow-lg shadow-orange-900/20 text-center">
                                Start Practicing →
                            </Link>
                        </div>
                    </div>
                </aside>
            </div>
        </article>
    );
};

export default BlogPost;
